import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import IPPortfolio from "@/components/IPPortfolio";
import { StarSpinner } from "@/components/StarSpinner";
import { toast } from "sonner";

const STATUS_COPY = {
  new: { label: "Received", color: "text-oxblood", bg: "bg-oxblood/10" },
  in_review: { label: "In Review", color: "text-navy", bg: "bg-navy/10" },
  contacted: { label: "Contacted", color: "text-hunter", bg: "bg-hunter/10" },
  engaged: { label: "Engaged", color: "text-brass2", bg: "bg-brass/15" },
  closed: { label: "Closed", color: "text-slate2", bg: "bg-slate2/10" },
};

export default function Portal() {
  const { user, logout, refresh } = useAuth();
  const [intakes, setIntakes] = useState(null);
  const [tab, setTab] = useState("matters");
  const [businessName, setBusinessName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    // `api` is a stable module-level import and setIntakes/setAssets identity
    // is stable across renders; running once on mount is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    api.get("/me/intakes").then(({ data }) => setIntakes(data)).catch((err) => {
      if (typeof console !== "undefined") console.warn("[portal] /me/intakes failed:", err);
      setIntakes([]);
    });
  }, []);

  useEffect(() => {
    if (user && typeof user === "object") {
      setBusinessName(user.business_name || "");
    }
  }, [user]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/auth/profile", { business_name: businessName });
      await refresh();
      toast.success("Business name saved");
    } catch (_) {
      toast.error("Could not save");
    } finally {
      setSavingProfile(false);
    }
  };

  const downloadCertificate = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/portal/ip-certificate.pdf", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = (user?.email || "client").replace("@", "-at-").replace(/\./g, "-");
      a.download = `LSC-IP-Stewardship-${slug}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
    } catch (_) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-ivory min-h-screen pt-32 pb-32" data-testid="portal-page">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-8 hairline-bottom pb-10">
          <div>
            <div className="kicker">Client Portal</div>
            <h1 className="mt-4 font-serif text-5xl md:text-7xl leading-[0.98]">
              Good morning,<br/>
              <span className="italic text-oxblood">{user?.name || "Counsel"}.</span>
            </h1>
            <p className="mt-6 text-slate2 font-light max-w-xl">
              A confidential summary of the matters and intellectual property currently before the firm on your behalf.
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/intake" className="btn-primary" data-testid="portal-new-intake">New Matter</Link>
            <button onClick={logout} className="btn-ghost" data-testid="portal-logout">Sign Out</button>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-3 hairline pt-6">
            <div className="kicker">Account</div>
            <div className="mt-4 font-serif text-2xl">{user?.name}</div>
            <div className="text-slate2 text-sm break-all">{user?.email}</div>
            <div className="mt-4 inline-block px-3 py-1 border border-transparent text-xs uppercase tracking-[0.2em] text-oxblood">
              {user?.role}
            </div>
          </aside>

          <div className="lg:col-span-9">
            {/* Tabs */}
            <div className="flex gap-8 hairline-bottom pb-4" data-testid="portal-tabs">
              <button
                onClick={() => setTab("matters")}
                className={`nav-link !text-base ${tab === "matters" ? "active text-oxblood" : ""}`}
                data-testid="portal-tab-matters"
              >
                Your Matters
              </button>
              <button
                onClick={() => setTab("ip")}
                className={`nav-link !text-base ${tab === "ip" ? "active text-oxblood" : ""}`}
                data-testid="portal-tab-ip"
              >
                IP Portfolio
              </button>
            </div>

            {tab === "matters" && (
              <div className="mt-8" data-testid="portal-matters-section">
                <div className="kicker">Your Matters</div>
                <h2 className="mt-3 font-serif text-3xl md:text-4xl">Current engagements</h2>

                {intakes === null && (
                  <div className="mt-12" data-testid="portal-loading">
                    <StarSpinner label="Loading matters" />
                  </div>
                )}
                {intakes && intakes.length === 0 && (
                  <div className="mt-12 p-12 border border-transparent bg-linen/40 text-center" data-testid="portal-empty">
                    <div className="font-serif italic text-2xl text-slate2">No matters are currently before the firm.</div>
                    <Link to="/intake" className="mt-6 btn-brass inline-flex" data-testid="portal-empty-cta">
                      Submit a Matter
                    </Link>
                  </div>
                )}

                {intakes && intakes.length > 0 && (
                  <div className="mt-10 space-y-px bg-brass/30" data-testid="portal-intakes-list">
                    {intakes.map((it) => {
                      const s = STATUS_COPY[it.status] || STATUS_COPY.new;
                      return (
                        <div key={it.id} className="bg-ivory p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-start" data-testid={`portal-intake-${it.id}`}>
                          <div className="md:col-span-3">
                            <div className="kicker">{it.practice_area}</div>
                            <div className="mt-2 font-serif italic text-2xl text-navy">{it.stage}</div>
                            <div className="mt-2 text-xs text-slate2/70 tracking-[0.2em] uppercase">
                              {new Date(it.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                            </div>
                          </div>
                          <div className="md:col-span-7">
                            <p className="font-serif text-lg text-ink leading-relaxed">"{it.message}"</p>
                            <div className="mt-4 flex flex-wrap gap-4 text-xs uppercase tracking-[0.2em] text-slate2/80">
                              {it.entity_type && <span>Entity: {it.entity_type}</span>}
                              {it.estimated_value && <span>Value: {it.estimated_value}</span>}
                              {it.timeline && <span>Timeline: {it.timeline}</span>}
                            </div>
                          </div>
                          <div className="md:col-span-2 md:text-right">
                            <span className={`inline-block px-3 py-1.5 ${s.bg} ${s.color} text-xs uppercase tracking-[0.2em] font-medium`}>
                              {s.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === "ip" && (
              <div className="mt-8" data-testid="portal-ip-section">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                  <div>
                    <div className="kicker">Intellectual Property</div>
                    <h2 className="mt-3 font-serif text-3xl md:text-4xl">
                      Portfolio under stewardship
                    </h2>
                    <p className="mt-4 max-w-xl text-slate2 font-light">
                      Trademarks, copyrights, domains, and patents the firm is protecting on your behalf.
                      Renewal calendar and prosecution status maintained in confidence.
                    </p>
                  </div>
                  <button
                    onClick={downloadCertificate}
                    disabled={downloading}
                    className="btn-brass shrink-0 disabled:opacity-70 inline-flex items-center gap-3"
                    data-testid="portal-ip-download-cert"
                  >
                    {downloading ? (
                      <>
                        <StarSpinner size="sm" label="" />
                        <span>Preparing…</span>
                      </>
                    ) : (
                      <span>Download Certificate (PDF)</span>
                    )}
                  </button>
                </div>

                {/* Business of record — populates on the PDF certificate */}
                <div className="mt-8 p-6 bg-linen/50 grid grid-cols-1 md:grid-cols-12 gap-4 items-end" data-testid="portal-business-name">
                  <div className="md:col-span-8">
                    <label className="kicker text-[0.62rem]" htmlFor="business_name">
                      Business of Record <span className="text-slate2 normal-case tracking-normal font-light">— appears on your Certificate of IP Stewardship</span>
                    </label>
                    <input
                      id="business_name"
                      type="text"
                      className="field mt-2"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="Doe Holdings LLC"
                      data-testid="portal-business-name-input"
                    />
                  </div>
                  <div className="md:col-span-4 md:text-right">
                    <button
                      onClick={saveProfile}
                      disabled={savingProfile}
                      className="btn-ghost disabled:opacity-50"
                      data-testid="portal-business-name-save"
                    >
                      {savingProfile ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>

                <IPPortfolio />
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
